import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Backdrop,
  Box,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
  Link,
  Snackbar,
} from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";

import AccountMenu from "../AccountMenu.js";

import { resetUser } from "../../../store/actions/user-actions";
import { clearListings } from "../../../store/actions/listing-actions.js";

import { isEmpty } from "../../../library/utils.js";
import { ContentCopy, Description, Sell } from "@mui/icons-material";
import promocodesStyles from "./promoCodesStyles.js";
import { fetchPromoCodes } from "../../../store/actions/promocodes-actions.js";

const drawerWidth = 240;

const PromoCodes = (props) => {
  const dispatch = useDispatch();
  const styles = promocodesStyles();
  const theme = useTheme();

  const userId = useSelector((state) => state.auth.user.id);
  const loadingUser = useSelector((state) => state.user.loading);
  const promocodes = useSelector((state) => state.promocodes.list);
  const loadingIdTypes = useSelector((state) => state.listing.loadingIdTypes);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbarVisibility, setSnackbarVisibility] = useState(false);
  const open = Boolean(snackbarVisibility);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    dispatch(fetchPromoCodes(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    dispatch(clearListings());
    dispatch(resetUser());
  }, [dispatch]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCopy = (event, strPromoCode) => {
    setSnackbarVisibility(true);
    navigator.clipboard.writeText(strPromoCode);
  };

  const handleClose = () => {
    setSnackbarVisibility(false);
  };

  return (
    <div className={styles.root}>
      <StyledEngineProvider injectFirst>
        <Box sx={{ display: "flex" }}>
          <Box
            component="nav"
            sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            aria-label="mailbox folders"
          >
            <Drawer
              // container={container}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: "block", sm: "none" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                  zIndex: 2,
                  backgroundColor:
                    theme.palette.specifics.accountNavBarDrawerBackgroundColor,
                  color: theme.palette.specifics.accountNavBarDrawerColor,
                },
              }}
            >
              {<AccountMenu history={props.history} />}
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: "none", sm: "block" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: drawerWidth,
                  zIndex: 2,
                  backgroundColor:
                    theme.palette.specifics.accountNavBarDrawerBackgroundColor,
                  color: theme.palette.specifics.accountNavBarDrawerColor,
                },
              }}
              open
            >
              {<AccountMenu history={props.history} />}
            </Drawer>
          </Box>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              width: { sm: `calc(100% - ${drawerWidth}px)` },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className={styles.divTableContainer}>
              <StyledEngineProvider injectFirst>
                <Card classes={{ root: styles.documentsCard }}>
                  <CardHeader
                    title={"Promo Codes"}
                    subheader="Check the availble promo codes."
                  />
                  <CardContent>
                    <Grid
                      container
                      direction="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box position="relative" mb={3} width="100%">
                        {!isEmpty(promocodes) && (
                          <List>
                            {promocodes.map((d, index) => {
                              const id = "copy" + index;
                              return (
                                <div key={index}>
                                  <ListItem alignItems="flex-start">
                                    <ListItemAvatar>
                                      <Avatar>
                                        <Sell />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={
                                        <Typography
                                          sx={{
                                            display: "inline",
                                            fontWeight: "bold",
                                          }}
                                          component="span"
                                        >
                                          {`${d?.promoCode} `}
                                          <Link
                                            component="button"
                                            aria-describedby={id}
                                            onClick={(e) => {
                                              handleCopy(e, d?.promoCode);
                                            }}
                                          >
                                            <ContentCopy
                                              sx={{ width: 15, height: 15 }}
                                            >
                                              <Description
                                                sx={{
                                                  color:
                                                    theme.palette.specifics
                                                      .accountNavBarItemIconColor,
                                                }}
                                              />
                                            </ContentCopy>
                                          </Link>
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography color={"gray"}>
                                          {d?.description}
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                  <Divider variant="inset" component="li" />
                                </div>
                              );
                            })}
                          </List>
                        )}
                      </Box>
                    </Grid>
                    <Snackbar
                      open={open}
                      autoHideDuration={6000}
                      onClose={handleClose}
                      message="Promo code copied."
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                      }}
                    />
                  </CardContent>
                </Card>
              </StyledEngineProvider>
            </div>
            <Backdrop
              className={styles.backdrop}
              style={{ position: "absolute" }}
              open={loadingUser || loadingIdTypes || false}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
          </Box>
        </Box>
      </StyledEngineProvider>
    </div>
  );
};

export default PromoCodes;
